import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { OpenShiftCardContainer } from "@src/appV2/redesign/Shift/Open/CardContainer";
import type { Offer, OpenShift, OpenShiftWorkplace } from "@src/appV2/redesign/Shift/Open/types";
import { Title } from "@src/appV2/redesign/system/ui-components";

interface NearestOpenShiftsListProps {
  readonly shifts: OpenShift[];
  readonly workplacesMap: Map<string, OpenShiftWorkplace>;
  readonly favoriteWorkplaceIds: Set<string>;
  readonly onClickOpenShift: (params: {
    shift: OpenShift;
    workplace: OpenShiftWorkplace;
    offer?: Offer;
  }) => void;
}

export const MAX_NEAREST_SHIFTS_TO_DISPLAY = 5;

/**
 * Displays nearest available shifts found via progressive distance expansion.
 * Capped to 5 shifts to avoid rendering performance issues.
 */
export function NearestOpenShiftsList(props: NearestOpenShiftsListProps) {
  const { shifts, workplacesMap, onClickOpenShift, favoriteWorkplaceIds } = props;

  if (shifts.length === 0) {
    return null;
  }

  return (
    <Stack spacing={5} sx={{ py: 5 }}>
      <Stack spacing={1}>
        <Title semibold variant="h5" component="h3">
          Nearest available shifts
        </Title>
      </Stack>

      {shifts.slice(0, MAX_NEAREST_SHIFTS_TO_DISPLAY).map((shift) => {
        const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
        if (!isDefined(workplace)) {
          return null;
        }

        return (
          <OpenShiftCardContainer
            key={shift.id}
            isFromNearestOpenShifts
            shift={shift}
            workplace={workplace}
            isFavoriteWorkplace={favoriteWorkplaceIds.has(workplace.id)}
            blockingRequirementsContext={undefined}
            workers={[]}
            friendsMap={new Map()}
            recentColleaguesMap={new Map()}
            onClick={({ offer }) => {
              onClickOpenShift({ shift, workplace, offer });
            }}
          />
        );
      })}
    </Stack>
  );
}
